<template>
    <b-card v-if="Boolean(order.uuid)"
        :class="{
            'protection-card justify-content-center': true,
            'no-hslp': noHslpOfferedByState,
            'pay-at-close': isPayAtClose,
    }">
        <template #header v-if="Boolean(title)">
            <h5>{{ title }}</h5>
        </template>
        <b-container fluid>
            <b-row class="d-flex justify-content-center">
                <b-img :src="cardBadgeClass" height="248" width="197"></b-img>
            </b-row>
            <b-row class="d-flex justify-content-center g-0" v-if="!isAgent">
                <p
                    class="card-text text-center"
                    v-html="cardInfoTxt"
                />                
            </b-row>
            <b-row class="d-flex justify-content-center" v-if="shouldShowHslpButton">
                <button
                    pill
                    class="gold-button"
                    type="button"
                    @click="cardBtnClick"
                    v-html="cardBtnTxt"
                />
            </b-row>
        </b-container>
    </b-card>
    <b-card v-else-if="!Boolean(order._uuid)" >
        <template #header v-if="title">
            <p>{{ title }}</p>
        </template>
        <b-row class="d-flex justify-content-center">
            <b-spinner
                class="spinner"
                small
            />
        </b-row>
    </b-card>
</template>

<script>
import './ProtectionCard.scss';
import {invitationMeta, isProtected, payAtClose, propertyState, hslpBadgeIcon, purchasedByAgent} from "@helpers/order";
import {stateHasHSLP} from "@helpers/state";
import {useLookupsStore} from '@store';
import {mapState, mapActions} from 'pinia';

export default {
    name: 'ProtectionCard',
    props: {
        order: {
            type: Object,
            required: true
        },
        title:{
            type: String,
            default: null
        },
        isAgent:{
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        minYr: 1,
        maxYr: 1,
    }),
    computed: {
        ...mapState(useLookupsStore, {
            getLuStatesAsArrayFromStore: 'getLuStatesAsArray',
            getLuStateIdsFromStore: 'getLuStateIds',
        }),
        noHslpOfferedByState() {
            let state = propertyState(this.order);
            if (!state?.length) {
                let stateId = this.order?.property?.stateId || (invitationMeta(this.order).property || {}).state;
                const states = this.getLuStatesAsArrayFromStore;
                if (!!stateId) {
                    state = states.find(state => state.id === parseInt(stateId)) || {};
                }
            }
            return state.meta ? !stateHasHSLP(JSON.parse(state.meta)) : false;
        },
        hasNoProtection() {
            return !this.noHslpOfferedByState && !isProtected(this.order) && !payAtClose(this.order);
        },
        isProtected() {
            return !this.noHslpOfferedByState && isProtected(this.order);
        },
        isPayAtClose() {
            return !this.noHslpOfferedByState && payAtClose(this.order);
        },
        cardBadgeClass() {
            return hslpBadgeIcon(this.order);
        },
        cardInfoTxt() {
            if(!this.isAgent){
                switch (true) {
                    case this.noHslpOfferedByState:
                        return 'Not available';
                    case this.isProtected:
                        return 'Your home sale is <b>protected!</b> Download your Certificate of Protection for more information.';
                    case this.isPayAtClose:
                        return `Add your closing information to protect your sale!`;
                }

                return `Protect yourself for up to 4 years after the sale - we've got your back!`;
            } else {
                switch(true){
                    case this.noHslpOfferedByState:
                        return 'Not Available';
                    case this.isProtected:
                        return 'This sale is protected by Sellers Shield!';
                    case this.isPayAtClose:
                        return this.getPacTextForAgent();
                }
                return `Protect you and your seller for up to 4 years after the sale - we've got your back!`;
            }
        },
        getPacTextForAgent(){
            let res = '';
            if(!purchasedByAgent(this.order)) {
                res = 'Your seller has reserved Home Sale Legal Protection&trade;';
            } else {
                res = 'You have reserved Home Sale Legal Protection&trade; for your seller.';
            }
            res += ' They will need to add their closing information to finalize their protection.';
        },
        cardBtnTxt() {
            switch (true) {
                case this.isProtected:
                    return 'Certificate of Protection';
                case this.isPayAtClose:
                    return 'Add Closing Information';
            }
            return 'Get Protection';
        },
        shouldShowHslpButton() {
            switch (true) {
                case this.noHslpOfferedByState:
                    return false;
                case this.isProtected:
                    return true;
                case this.isPayAtClose:
                    return !this.isAgent;
                case this.hasNoProtection:
                    return true;
            }
        },
    },
    methods: {
        cardBtnClick(event) {
            switch (true) {
                case this.isProtected:
                    this.redirectToCertificateProtection();
                    break;
                case this.isPayAtClose:
                    this.$bus.$emit('show-closing-information-modal', this.order);
                    break;
                case this.hasNoProtection:
                    this.$bus.$emit('show-hslp-modal', { orderAppResource: this.order});
                    break;
            }
            event.stopPropagation();
        },
        async redirectToCertificateProtection() {
            await this.$router.push({
                name: 'order-protection',
                params: {
                    uuid: this.order.uuid,
                }
            });
        },
    },
}
</script>